// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { defer } from 'lodash/fp';
import { Capacitor } from '@capacitor/core';

// Utils
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { PinchGestureState, TrackpadPinchPlugin } from '../../../capacitor_plugins/trackpadPinch';
import { CAPACITOR_TRACKPAD_PINCH_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { getZoomDirection, getZoomNextScaleOnTouch, ZOOM_DIRECTIONS } from './zoomTouchUtils';

/**
 * Attaches Capacitor trackpad pinch plugin to zoom functionality.
 * Based on `ZoomTouchHandler`.
 */
class ZoomIpadTrackpadHandler extends React.Component {
    componentDidMount() {
        defer(() => {
            if (Capacitor.isPluginAvailable(CAPACITOR_TRACKPAD_PINCH_PLUGIN)) {
                TrackpadPinchPlugin.addListener('trackpadPinch', this.handleTrackpadPinch);
            }
        });
    }

    componentWillUnmount() {
        if (!Capacitor.isPluginAvailable(CAPACITOR_TRACKPAD_PINCH_PLUGIN)) return;
        TrackpadPinchPlugin.removeAllListeners();
    }

    handleTrackpadPinch = (data) => {
        switch (data.state) {
            case PinchGestureState.began:
                this.handleTrackpadPinchBegan();
                break;
            case PinchGestureState.changed:
                this.handleTrackpadPinchChanged(data);
                break;
            case PinchGestureState.ended:
                // pinch ended: reset
                this.trackpadScaleStart = null;
                break;
            default:
                break;
        }
    };

    handleTrackpadPinchBegan = () => {
        // pinch began: record the start scale
        const { getZoomCanvasTransformationMatrix, dispatchFinishEditingCurrentElement } = this.props;

        dispatchFinishEditingCurrentElement();
        const m = getZoomCanvasTransformationMatrix();
        this.trackpadScaleStart = matrixLib.getScale(m);
        this.zoomDirection = ZOOM_DIRECTIONS.UNKNOWN;
    };

    handleTrackpadPinchChanged = ({ scale, velocity, centre }) => {
        // pinch changed: compute next scale and zoom to center
        if (this.trackpadScaleStart == null) return;
        const { domElementPropertyCache, canvasViewportRef, immediatelyScaleTo, canvasDocumentSize } = this.props;
        const { canvasViewportClientRect } = domElementPropertyCache.getCachedValues(canvasViewportRef.current);

        const nextScale = this.trackpadScaleStart * scale;

        // This logic mimics the logic in ZoomTouchHandler - onTouchMove.
        // When the pinch change is very small, do not determine the zoom direction.
        // This is will then affect the clamping logic in `getZoomNextScaleOnIpadTrackpad`,
        // resulting in no unintended jumps to 100% zoom.
        const ratioChange = nextScale / this.trackpadScaleStart;
        this.zoomDirection = getZoomDirection(ratioChange, this.zoomDirection);

        const adjustedNextScale = getZoomNextScaleOnTouch(
            canvasViewportClientRect,
            canvasDocumentSize,
            nextScale,
            this.trackpadScaleStart,
            this.zoomDirection,
        );
        immediatelyScaleTo(adjustedNextScale, { eventPosition: centre });
    };

    handleTrackpadPinchEnded = () => {
        this.trackpadScaleStart = null;
    };

    render() {
        return null;
    }
}

ZoomIpadTrackpadHandler.propTypes = {
    canvasViewportRef: PropTypes.object,
    canvasDocumentSize: PropTypes.object,

    getZoomCanvasTransformationMatrix: PropTypes.func,
    domElementPropertyCache: PropTypes.object,

    immediatelyScaleTo: PropTypes.func,
    dispatchFinishEditingCurrentElement: PropTypes.func,
};

export default ZoomIpadTrackpadHandler;
